import api from "./api";
import { getAll, getAllPage, getPage } from "./pagination";

export interface Student {
  id: number;
  user: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    phone_number?: string;
  };
  student_id: string;
  admission_number: string;
  /** What the SurePay daily report identifies this family's fee payments by. */
  surepay_number?: string;
  /** Day or boarding decides which fees the learner is billed. */
  residence?: "day" | "boarding";
  date_of_birth: string;
  gender: "male" | "female";
  blood_group?: string;
  nationality: string;
  current_class?: number | null;
  admission_date: string;
  status: "active" | "inactive" | "graduated" | "transferred" | "suspended" | "withdrawn" | "expelled";
  medical_conditions?: string;
  allergies?: string;
  medications?: string;
  doctor_name?: string;
  doctor_phone?: string;
  uses_transport: boolean;
  transport_route?: string;
  pickup_location?: string;
  previous_school?: string;
  notes?: string;
  age: number;
  is_active: boolean;
  full_name: string;
  current_class_name?: string;
  parents_data?: Parent[];
  primary_parent?: number | null;
}

export interface Parent {
  id: number;
  user: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    phone_number?: string;
  };
  relationship: string;
  occupation?: string;
  workplace?: string;
  emergency_contact?: string;
  address?: string;
  full_name: string;
  children_data?: Array<{
    id: number;
    full_name: string;
    student_id: string;
    class_name?: string | null;
    is_primary: boolean;
  }>;
  /** Whether the portal login works. */
  portal_active?: boolean;
  last_login?: string | null;
  /** A temporary password is waiting to be changed at the next sign-in. */
  must_change_password?: boolean;
  created_at?: string;
}

export interface ParentSummary {
  total: number;
  unlinked: number;
  inactive: number;
  never_signed_in: number;
  no_phone: number;
  learners_without_parent: number;
}

export type ParentListParams = {
  search?: string;
  relationship?: string;
  ordering?: string;
  linked?: "true" | "false";
  portal?: "active" | "inactive" | "never";
  child_class?: number;
  no_phone?: "true";
}

export interface LearnerWithoutParent {
  id: number;
  full_name: string;
  student_id: string;
  admission_number: string;
  class_name: string | null;
  admission_date: string;
}

export interface AccessChange {
  id: number;
  action: string;
  action_label: string;
  reason: string;
  changed_by: string;
  created_at: string;
}

export interface Class {
  id: number;
  name: string;
  level: string;
  section?: string | null;
  capacity: number;
  academic_year: number;
  academic_year_name: string;
  class_teacher?: number | null;
  class_teacher_name?: string | null;
  room?: string;
  notes?: string;
  /** Learners in school (status active) now in the class. */
  current_student_count: number;
  boys?: number;
  girls?: number;
  available_spots: number;
}

/** One class opened: who is in it, who teaches it, and what deleting it would touch. */
export interface ClassDetail extends Class {
  learners: Array<{ id: number; full_name: string; student_id: string; admission_number: string; gender: string }>;
  subject_teachers: Array<{ id: number; subject: string; teacher: string }>;
  in_use: Record<string, number>;
}

export interface ClassCopyYearInput {
  from_year: number;
  to_year: number;
  keep_teachers: boolean;
}

export interface ClassCopyYearResponse {
  created_count: number;
  skipped: string[];
  classes: Class[];
}

export interface ClassTeacherCandidate {
  id: number;
  full_name: string;
  user_type: string;
}

export interface BulkClassCreateInput {
  academic_year: number;
  level: string;
  streams: Array<{
    section?: string | null;
    name?: string;
    capacity: number;
    room?: string;
    class_teacher?: number | null;
  }>;
}

export interface BulkClassCreateResponse {
  created_count: number;
  classes: Class[];
}

export interface Attendance {
  id: number;
  student: {
    id: number;
    user: {
      first_name: string;
      last_name: string;
    };
    student_id: string;
  };
  date: string;
  status: "present" | "absent" | "late" | "excused";
  remarks?: string;
  marked_by?: {
    id: number;
    username: string;
    first_name: string;
    last_name: string;
  };
  student_name: string;
  student_id: string;
  marked_by_name?: string;
}

export const studentService = {
  // Students
  async getStudents(params?: {
    search?: string;
    status?: string;
    gender?: string;
    current_class?: number;
    uses_transport?: boolean;
    residence?: string;
    ordering?: string;
    page?: number;
    page_size?: number;
  }): Promise<{ results: Student[]; count: number; next?: string | null; previous?: string | null }> {
    const response = await api.get<{ results: Student[]; count: number; next?: string | null; previous?: string | null }>("/students/", {
      params,
    });
    return response.data;
  },

  async getStudentsPage(params: {
    page: number;
    page_size?: number;
    search?: string;
    status?: string;
    gender?: string;
    current_class?: number;
    uses_transport?: boolean;
    residence?: string;
    ordering?: string;
  }): Promise<{ results: Student[]; count: number }> {
    return getPage<Student>("/students/", params);
  },

  async getLifecycleStudents(): Promise<{ results: Student[]; count: number }> {
    return getAllPage<Student>("/students/", {
      ordering: "user__first_name,user__last_name,id",
    });
  },

  async getStudent(id: number): Promise<Student> {
    const response = await api.get(`/students/${id}/`);
    return response.data;
  },

  async createStudent(studentData: any): Promise<Student> {
    const response = await api.post("/students/", studentData);
    return response.data;
  },

  async updateStudent(id: number, studentData: any): Promise<Student> {
    const response = await api.patch(`/students/${id}/`, studentData);
    return response.data;
  },

  async deleteStudent(id: number): Promise<void> {
    await api.delete(`/students/${id}/`);
  },

  // Classes
  async getClasses(params?: {
    search?: string;
    level?: string;
    academic_year?: number;
    ordering?: string;
  }): Promise<{ results: Class[]; count: number }> {
    const requestedOrdering =
      params?.ordering === "level"
        ? "level,section,name,id"
        : (params?.ordering ?? "level,section,name,id");
    const orderingFields = requestedOrdering.split(",");
    const stableOrdering = orderingFields.some(
      (field) => field.replace(/^-/, "") === "id",
    )
      ? requestedOrdering
      : `${requestedOrdering},id`;
    return getAllPage<Class>("/students/classes/", {
      ...params,
      ordering: stableOrdering,
    });
  },

  async getClass(id: number): Promise<ClassDetail> {
    const response = await api.get(`/students/classes/${id}/`);
    return response.data;
  },

  async getClassTeacherCandidates(): Promise<ClassTeacherCandidate[]> {
    return getAll<ClassTeacherCandidate>(
      "/students/classes/teacher-candidates/",
    );
  },

  async createClass(classData: any): Promise<Class> {
    const response = await api.post("/students/classes/", classData);
    return response.data;
  },

  async createClasses(
    classData: BulkClassCreateInput,
  ): Promise<BulkClassCreateResponse> {
    const response = await api.post("/students/classes/bulk/", classData);
    return response.data;
  },

  /** Copies an earlier year's classes into another year, skipping names it already has. */
  async copyClassesFromYear(data: ClassCopyYearInput): Promise<ClassCopyYearResponse> {
    const response = await api.post("/students/classes/copy-year/", data);
    return response.data;
  },

  async updateClass(id: number, classData: any): Promise<Class> {
    const response = await api.patch(`/students/classes/${id}/`, classData);
    return response.data;
  },

  async deleteClass(id: number): Promise<void> {
    await api.delete(`/students/classes/${id}/`);
  },

  // Parents
  async getParents(params?: ParentListParams): Promise<{ results: Parent[]; count: number }> {
    return getAllPage<Parent>("/students/parents/", params);
  },

  async getParentsPage(params: ParentListParams & {
    page: number;
    page_size?: number;
  }): Promise<{ results: Parent[]; count: number }> {
    return getPage<Parent>("/students/parents/", params);
  },

  async getParentSummary(): Promise<ParentSummary> {
    return (await api.get("/students/parents/summary/")).data;
  },

  async getLearnersWithoutParent(): Promise<LearnerWithoutParent[]> {
    return (await api.get("/students/parents/learners-without-parent/")).data;
  },

  /** Turns the portal login off or back on, with the reason kept. */
  async setParentPortal(id: number, active: boolean, reason: string): Promise<Parent> {
    return (await api.post(`/students/parents/${id}/portal/`, { active, reason })).data;
  },

  /** A new temporary password, made by the server and returned once. */
  async resetParentPassword(id: number, reason: string): Promise<{ username: string; temporary_password: string }> {
    return (await api.post(`/students/parents/${id}/reset-password/`, { reason })).data;
  },

  async getParentAccessHistory(id: number): Promise<AccessChange[]> {
    return (await api.get(`/students/parents/${id}/access-history/`)).data;
  },

  /** Search-as-you-type lookup: only the first page of matches, so typing
   * into a live search box doesn't drain every page of a common surname
   * (mirrors financialService.searchStudents' single-page convention). */
  async searchParents(query: string): Promise<Parent[]> {
    if (query.trim().length < 2) return [];
    const response = await api.get("/students/parents/", { params: { search: query } });
    return response.data.results as Parent[];
  },

  async getParent(id: number): Promise<Parent> {
    const response = await api.get(`/students/parents/${id}/`);
    return response.data;
  },

  async createParent(parentData: any): Promise<Parent> {
    const response = await api.post("/students/parents/", parentData);
    return response.data;
  },

  async updateParent(id: number, parentData: any): Promise<Parent> {
    const response = await api.patch(`/students/parents/${id}/`, parentData);
    return response.data;
  },

  async deleteParent(id: number): Promise<void> {
    await api.delete(`/students/parents/${id}/`);
  },

  async linkParentChildren(
    id: number,
    data: { student_ids: number[]; primary_student_ids: number[] },
  ): Promise<Parent> {
    const response = await api.put(`/students/parents/${id}/children/`, data);
    return response.data;
  },

  // Attendance
  async getAttendance(params?: {
    student?: number;
    date?: string;
    status?: string;
    ordering?: string;
  }): Promise<{ results: Attendance[]; count: number }> {
    return getAllPage<Attendance>("/students/attendance/", params);
  },

  async getAttendanceRecord(id: number): Promise<Attendance> {
    const response = await api.get(`/students/attendance/${id}/`);
    return response.data;
  },

  async createAttendance(attendanceData: any): Promise<Attendance> {
    const response = await api.post("/students/attendance/", attendanceData);
    return response.data;
  },

  async updateAttendance(id: number, attendanceData: any): Promise<Attendance> {
    const response = await api.patch(
      `/students/attendance/${id}/`,
      attendanceData,
    );
    return response.data;
  },

  async deleteAttendance(id: number): Promise<void> {
    await api.delete(`/students/attendance/${id}/`);
  },

  async bulkAttendance(attendanceRecords: any[]): Promise<void> {
    await api.post("/students/attendance/bulk/", {
      attendance_records: attendanceRecords,
    });
  },

  // Statistics
  async getStudentStats(): Promise<{
    total_students: number;
    total_classes: number;
    attendance_today: number;
  }> {
    const response = await api.get("/students/stats/");
    return response.data;
  },
};
